import { CanvasView } from './canvasView.js';
import { FileManager, setupDropzone, renderFileList } from './fileManager.js';
import { WorkerClient } from '../workers/workerClient.js';
import { decodeImageToRgba, encodeRgbaToBlob } from '../io/imageCodec.js';
import { buildZip, downloadBlob } from '../io/zip.js';
import { announceCancelled, announceSaved, bindSaveLocation, saveLocationMarkup } from './saveLocation.js';
import { showToast } from './toast.js';
import { withProgress } from './progress.js';
import type { DpiMode, LoadedFile, LockMode } from '../types.js';

const DEFAULT_DPI = 72;

interface FileDpiInfo {
  widthPx: number;
  heightPx: number;
  dpi: { x: number; y: number };
  isPdf: boolean;
  pageCount?: number;
  pageSizeInches?: { width: number; height: number };
}

export function mountDpiTab(panel: HTMLElement): void {
  panel.innerHTML = '';
  panel.appendChild(buildMarkup());

  const fileManager = new FileManager();
  const dpiWorker = new WorkerClient(new Worker(new URL('../workers/dpi.worker.ts', import.meta.url), { type: 'module' }));

  const canvasViewport = panel.querySelector('.canvas-viewport') as HTMLDivElement;
  const canvasView = new CanvasView(canvasViewport);
  canvasView.setBackground('checkerboard');

  const zoomOutBtn = panel.querySelector('#zoom-out-dpi') as HTMLButtonElement;
  const zoomInBtn = panel.querySelector('#zoom-in-dpi') as HTMLButtonElement;
  const zoomResetBtn = panel.querySelector('#zoom-reset-dpi') as HTMLButtonElement;
  const zoomLevelOut = panel.querySelector('#zoom-level-dpi') as HTMLOutputElement;
  const updateZoomUi = (percent: number) => {
    zoomLevelOut.textContent = `${percent}%`;
    zoomOutBtn.disabled = !canvasView.canZoomOut();
    zoomInBtn.disabled = !canvasView.canZoomIn();
  };
  canvasView.setOnZoomChange(updateZoomUi);
  zoomInBtn.addEventListener('click', () => canvasView.zoomIn());
  zoomOutBtn.addEventListener('click', () => canvasView.zoomOut());
  zoomResetBtn.addEventListener('click', () => canvasView.zoomReset());
  updateZoomUi(canvasView.getZoomPercent());

  const el = <T extends HTMLElement>(sel: string) => panel.querySelector(sel) as T;
  const dropzone = el<HTMLDivElement>('#dpi-dropzone');
  const fileList = el<HTMLUListElement>('#dpi-file-list');
  const currentReadout = el<HTMLDivElement>('#dpi-current-readout');
  const projectedReadout = el<HTMLDivElement>('#dpi-projected-readout');

  const modeSeg = el<HTMLDivElement>('#dpi-mode');
  const presetSel = el<HTMLSelectElement>('#dpi-preset');
  const customInput = el<HTMLInputElement>('#dpi-custom');
  const lockSeg = el<HTMLDivElement>('#dpi-lock');
  const filterSeg = el<HTMLDivElement>('#dpi-filter');

  const pdfSection = el<HTMLDivElement>('#dpi-pdf-section');
  const pdfModeSeg = el<HTMLDivElement>('#dpi-pdf-mode');
  const pageRangeInput = el<HTMLInputElement>('#dpi-page-range');
  const pdfOutputFormatSel = el<HTMLSelectElement>('#dpi-pdf-output-format');

  const outFormatSel = el<HTMLSelectElement>('#dpi-out-format');
  const jpegQualityInput = el<HTMLInputElement>('#dpi-jpeg-quality');
  const jpegQualityOut = el<HTMLOutputElement>('#dpi-jpeg-quality-out');
  const jpegQualityRow = el<HTMLDivElement>('#dpi-jpeg-quality-row');
  const suffixInput = el<HTMLInputElement>('#dpi-suffix');
  const preserveMetaToggle = el<HTMLInputElement>('#dpi-preserve-metadata');
  const applyBtn = el<HTMLButtonElement>('#dpi-apply');
  const applyAllBtn = el<HTMLButtonElement>('#dpi-apply-all');
  bindSaveLocation(panel, 'dpi');

  let info: FileDpiInfo | null = null;
  let activeFile: LoadedFile | null = null;
  /** Bumped on every load/unload so a slow load (big PDF) finishing after its file was closed is ignored. */
  let loadToken = 0;

  /** Clears the preview and readouts after the last file is closed. */
  function unloadFile(): void {
    loadToken++;
    activeFile = null;
    info = null;
    canvasView.clear();
    pdfSection.hidden = true;
    currentReadout.textContent = '';
    projectedReadout.textContent = '';
  }

  function getMode(): DpiMode {
    return (modeSeg.querySelector('[aria-pressed="true"]') as HTMLButtonElement)?.dataset.mode === 'resample' ? 'resample' : 'tagOnly';
  }
  function getLockMode(): LockMode {
    return (lockSeg.querySelector('[aria-pressed="true"]') as HTMLButtonElement)?.dataset.lock === 'pixelSize' ? 'pixelSize' : 'printSize';
  }
  function getFilter(): 'lanczos3' | 'bicubic' {
    return (filterSeg.querySelector('[aria-pressed="true"]') as HTMLButtonElement)?.dataset.filter === 'bicubic' ? 'bicubic' : 'lanczos3';
  }
  function getPdfMode(): 'rasterize' | 'downsample' {
    return (pdfModeSeg.querySelector('[aria-pressed="true"]') as HTMLButtonElement)?.dataset.pdfmode === 'downsample' ? 'downsample' : 'rasterize';
  }
  function getTargetDpi(): number {
    if (presetSel.value === 'custom') return Number(customInput.value) || DEFAULT_DPI;
    return Number(presetSel.value);
  }

  function setupSegmented(container: HTMLElement, onChange?: () => void): void {
    for (const btn of Array.from(container.querySelectorAll('button'))) {
      btn.addEventListener('click', () => {
        for (const b of Array.from(container.querySelectorAll('button'))) b.setAttribute('aria-pressed', 'false');
        btn.setAttribute('aria-pressed', 'true');
        onChange?.();
        updateProjection();
      });
    }
  }
  setupSegmented(modeSeg, updateModeVisibility);
  setupSegmented(lockSeg);
  setupSegmented(filterSeg);
  setupSegmented(pdfModeSeg, updatePdfModeVisibility);

  const customRow = customInput.closest('.field-row') as HTMLElement;
  customRow.hidden = true; // the whole row, not just the input, or an orphan "Custom DPI" label is left behind
  presetSel.addEventListener('change', () => {
    customRow.hidden = presetSel.value !== 'custom';
    updateProjection();
  });
  customInput.addEventListener('input', updateProjection);
  outFormatSel.addEventListener('change', () => {
    jpegQualityRow.hidden = outFormatSel.value !== 'jpeg';
  });
  jpegQualityInput.addEventListener('input', () => (jpegQualityOut.textContent = jpegQualityInput.value));

  function updateModeVisibility(): void {
    const mode = getMode();
    lockSeg.closest('.field-row')!.toggleAttribute('hidden', mode !== 'resample');
    filterSeg.closest('.field-row')!.toggleAttribute('hidden', mode !== 'resample');
  }
  function updatePdfModeVisibility(): void {
    const mode = getPdfMode();
    pageRangeInput.closest('.field-row')!.toggleAttribute('hidden', false);
    pdfOutputFormatSel.closest('.field-row')!.toggleAttribute('hidden', mode !== 'rasterize');
  }
  updateModeVisibility();
  updatePdfModeVisibility();

  function updateProjection(): void {
    if (!info) {
      projectedReadout.textContent = '';
      return;
    }
    const targetDpi = getTargetDpi();
    const mode = getMode();
    if (info.isPdf && info.pageSizeInches) {
      // PDFs have no DPI tag: pages are either rasterized at the target DPI or their embedded images downsampled.
      const { width, height } = info.pageSizeInches;
      if (getPdfMode() === 'downsample') {
        projectedReadout.textContent = `Embedded images above ${targetDpi} DPI (for a ${width.toFixed(2)}in × ${height.toFixed(2)}in page) are downsampled; page size and text stay as they are.`;
      } else {
        projectedReadout.textContent = `Each page rasterized at ${targetDpi} DPI → ${Math.round(width * targetDpi)} × ${Math.round(height * targetDpi)}px.`;
      }
      return;
    }
    if (mode === 'tagOnly') {
      const widthIn = info.widthPx / info.dpi.x;
      const heightIn = info.heightPx / info.dpi.y;
      const newWidthIn = info.widthPx / targetDpi;
      const newHeightIn = info.heightPx / targetDpi;
      projectedReadout.textContent = `Pixels unchanged: ${info.widthPx} × ${info.heightPx}px. DPI tag → ${targetDpi}, so print size goes from ${widthIn.toFixed(2)}in × ${heightIn.toFixed(2)}in to ${newWidthIn.toFixed(2)}in × ${newHeightIn.toFixed(2)}in.`;
      return;
    }
    const lockMode = getLockMode();
    const widthIn = info.widthPx / info.dpi.x;
    const heightIn = info.heightPx / info.dpi.y;
    if (lockMode === 'printSize') {
      const newW = Math.max(1, Math.round(widthIn * targetDpi));
      const newH = Math.max(1, Math.round(heightIn * targetDpi));
      projectedReadout.textContent = `${newW} × ${newH}px — print size stays ${widthIn.toFixed(2)}in × ${heightIn.toFixed(2)}in (${(widthIn * 2.54).toFixed(2)}cm × ${(heightIn * 2.54).toFixed(2)}cm).`;
    } else {
      const newWidthIn = info.widthPx / targetDpi;
      const newHeightIn = info.heightPx / targetDpi;
      projectedReadout.textContent = `Pixels stay ${info.widthPx} × ${info.heightPx}px — print size becomes ${newWidthIn.toFixed(2)}in × ${newHeightIn.toFixed(2)}in.`;
    }
  }

  async function loadFile(file: LoadedFile): Promise<void> {
    activeFile = file;
    const token = ++loadToken;
    try {
      if (file.sourceFormat === 'pdf') {
        const pdfInfo = await dpiWorker.call<{ pageCount: number; pageSizeInches: { width: number; height: number } }>('getPdfInfo', {
          bytes: file.originalBytes.slice(0),
        });
        const previewDpi = 100;
        const rasterized = await dpiWorker.call<{ width: number; height: number; rgba: ArrayBuffer }[]>('rasterizePdf', {
          bytes: file.originalBytes.slice(0),
          dpi: previewDpi,
          pageNumbers: [1],
        });
        const page = rasterized[0];
        if (token !== loadToken) return; // closed or switched while rasterizing
        canvasView.setImage(page.width, page.height, new Uint8ClampedArray(page.rgba));
        info = {
          widthPx: Math.round(pdfInfo.pageSizeInches.width * previewDpi),
          heightPx: Math.round(pdfInfo.pageSizeInches.height * previewDpi),
          dpi: { x: previewDpi, y: previewDpi },
          isPdf: true,
          pageCount: pdfInfo.pageCount,
          pageSizeInches: pdfInfo.pageSizeInches,
        };
        pdfSection.hidden = false;
        pageRangeInput.placeholder = `e.g. 1-${pdfInfo.pageCount}`;
        currentReadout.textContent = `${pdfInfo.pageCount} page(s) · page size ${pdfInfo.pageSizeInches.width.toFixed(2)}in × ${pdfInfo.pageSizeInches.height.toFixed(2)}in`;
      } else {
        const blob = new Blob([file.originalBytes], { type: file.sourceFormat === 'png' ? 'image/png' : 'image/jpeg' });
        const decoded = await decodeImageToRgba(blob);
        const dpiResult = await dpiWorker.call<{ dpi: { x: number; y: number } | null; widthPx: number; heightPx: number }>('readDpi', {
          bytes: file.originalBytes.slice(0),
        });
        if (token !== loadToken) return;
        canvasView.setImage(decoded.width, decoded.height, decoded.rgba);
        const dpi = dpiResult.dpi ?? { x: DEFAULT_DPI, y: DEFAULT_DPI };
        info = { widthPx: decoded.width, heightPx: decoded.height, dpi, isPdf: false };
        pdfSection.hidden = true;
        currentReadout.textContent = `${decoded.width} × ${decoded.height}px · ${dpiResult.dpi ? Math.round(dpi.x) + ' DPI' : 'no DPI tag (assuming 72)'}`;
      }
      updateProjection();
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to load file', true);
    }
  }

  fileManager.onChange = (files, activeId) => {
    renderFileList(fileList, files, activeId, (id) => fileManager.setActive(id), (id) => fileManager.remove(id));
    applyAllBtn.disabled = files.length < 2;
    const active = fileManager.getActive();
    if (active) void loadFile(active);
    else unloadFile();
  };

  setupDropzone(dropzone, (files) => {
    fileManager.addFiles(files).catch((err) => showToast(err.message, true));
  });

  async function applyToFile(file: LoadedFile): Promise<{ filename: string; blob: Blob }[]> {
    const targetDpi = getTargetDpi();
    const mode = getMode();
    const suffix = suffixInput.value || `_${targetDpi}dpi`;
    const baseName = file.name.replace(/\.[^.]+$/, '');

    if (file.sourceFormat === 'pdf') {
      const pdfInfo = await dpiWorker.call<{ pageCount: number; pageSizeInches: { width: number; height: number } }>('getPdfInfo', {
        bytes: file.originalBytes.slice(0),
      });
      const pages = await dpiWorker.call<{ pages: number[] }>('parsePdfPageRange', { rangeText: pageRangeInput.value, pageCount: pdfInfo.pageCount });

      if (getPdfMode() === 'downsample') {
        const result = await dpiWorker.call<{ bytes: ArrayBuffer }>('downsamplePdfImages', {
          bytes: file.originalBytes.slice(0),
          targetDpi,
          pageSizeInches: pdfInfo.pageSizeInches,
        });
        return [{ filename: `${baseName}${suffix}.pdf`, blob: new Blob([result.bytes], { type: 'application/pdf' }) }];
      }

      const rasterized = await dpiWorker.call<{ width: number; height: number; rgba: ArrayBuffer; pageNumber: number; dpi: number }[]>('rasterizePdf', {
        bytes: file.originalBytes.slice(0),
        dpi: targetDpi,
        pageNumbers: pages.pages,
      });
      const outFormat = pdfOutputFormatSel.value;
      if (outFormat === 'pdf') {
        const built = await dpiWorker.call<{ bytes: ArrayBuffer }>('buildPdfFromPages', {
          pages: rasterized.map((p) => ({ width: p.width, height: p.height, rgba: p.rgba, dpi: p.dpi })),
          format: 'png',
        });
        return [{ filename: `${baseName}${suffix}.pdf`, blob: new Blob([built.bytes], { type: 'application/pdf' }) }];
      }
      const results: { filename: string; blob: Blob }[] = [];
      for (const p of rasterized) {
        const blob = await encodeRgbaToBlob(new Uint8ClampedArray(p.rgba), p.width, p.height, outFormat as 'png' | 'jpeg', Number(jpegQualityInput.value));
        results.push({ filename: `${baseName}_p${p.pageNumber}${suffix}.${outFormat === 'png' ? 'png' : 'jpg'}`, blob });
      }
      return results;
    }

    // PNG / JPEG
    const currentDpiResult = await dpiWorker.call<{ dpi: { x: number; y: number } | null; widthPx: number; heightPx: number }>('readDpi', {
      bytes: file.originalBytes.slice(0),
    });
    const currentDpi = currentDpiResult.dpi ?? { x: DEFAULT_DPI, y: DEFAULT_DPI };
    const ext = file.sourceFormat === 'png' ? 'png' : 'jpg';
    const outFormat = outFormatSel.value as 'png' | 'jpeg';

    if (mode === 'tagOnly') {
      const mime = file.sourceFormat === 'png' ? 'image/png' : 'image/jpeg';
      if (preserveMetaToggle.checked) {
        // In-place byte patch: every other chunk/segment (ICC profile, EXIF, text, thumbnails) is untouched.
        const result = await dpiWorker.call<{ bytes: ArrayBuffer }>('writeDpiTag', { bytes: file.originalBytes.slice(0), dpi: { x: targetDpi, y: targetDpi } });
        return [{ filename: `${baseName}${suffix}.${ext}`, blob: new Blob([result.bytes], { type: mime }) }];
      }
      // Strip metadata: re-encode from decoded pixels (drops EXIF/ICC/ancillary chunks), then tag DPI.
      const decodedForStrip = await decodeImageToRgba(new Blob([file.originalBytes], { type: mime }));
      const strippedBlob = await encodeRgbaToBlob(decodedForStrip.rgba, decodedForStrip.width, decodedForStrip.height, outFormat, Number(jpegQualityInput.value));
      const strippedBytes = new Uint8Array(await strippedBlob.arrayBuffer());
      const result = await dpiWorker.call<{ bytes: ArrayBuffer }>('writeDpiTag', { bytes: strippedBytes.buffer, dpi: { x: targetDpi, y: targetDpi } });
      const outMime = outFormat === 'png' ? 'image/png' : 'image/jpeg';
      return [{ filename: `${baseName}${suffix}.${outFormat === 'png' ? 'png' : 'jpg'}`, blob: new Blob([result.bytes], { type: outMime }) }];
    }

    // Resample mode
    const lockMode = getLockMode();
    const widthIn = currentDpiResult.widthPx / currentDpi.x;
    const heightIn = currentDpiResult.heightPx / currentDpi.y;
    const newWidth = lockMode === 'printSize' ? Math.max(1, Math.round(widthIn * targetDpi)) : currentDpiResult.widthPx;
    const newHeight = lockMode === 'printSize' ? Math.max(1, Math.round(heightIn * targetDpi)) : currentDpiResult.heightPx;

    if (lockMode === 'pixelSize') {
      // Pixels stay identical; only the DPI tag (and reported print size) change.
      const result = await dpiWorker.call<{ bytes: ArrayBuffer }>('writeDpiTag', { bytes: file.originalBytes.slice(0), dpi: { x: targetDpi, y: targetDpi } });
      const mime = file.sourceFormat === 'png' ? 'image/png' : 'image/jpeg';
      return [{ filename: `${baseName}${suffix}.${ext}`, blob: new Blob([result.bytes], { type: mime }) }];
    }

    const blob = file.sourceFormat === 'png' ? new Blob([file.originalBytes], { type: 'image/png' }) : new Blob([file.originalBytes], { type: 'image/jpeg' });
    const decoded = await decodeImageToRgba(blob);
    const resized = await dpiWorker.call<{ rgba: ArrayBuffer; width: number; height: number }>('resize', {
      rgba: decoded.rgba.buffer.slice(0),
      width: decoded.width,
      height: decoded.height,
      dstWidth: newWidth,
      dstHeight: newHeight,
      filter: getFilter(),
    });
    const encodedBlob = await encodeRgbaToBlob(new Uint8ClampedArray(resized.rgba), resized.width, resized.height, outFormat, Number(jpegQualityInput.value));
    const encodedBytes = new Uint8Array(await encodedBlob.arrayBuffer());
    const tagged = await dpiWorker.call<{ bytes: ArrayBuffer }>('writeDpiTag', { bytes: encodedBytes.buffer, dpi: { x: targetDpi, y: targetDpi } });
    const mime = outFormat === 'png' ? 'image/png' : 'image/jpeg';
    return [{ filename: `${baseName}${suffix}.${outFormat === 'png' ? 'png' : 'jpg'}`, blob: new Blob([tagged.bytes], { type: mime }) }];
  }

  applyBtn.addEventListener('click', () => {
    if (!activeFile) return showToast('Load a file first', true);
    void withProgress('Applying DPI changes…', async () => {
      const results = await applyToFile(activeFile!);
      let saved: { where: string } | null;
      let name: string;
      if (results.length === 1) {
        name = results[0].filename;
        saved = await downloadBlob(results[0].blob, name);
      } else {
        const zip = await buildZip(results.map((r) => ({ filename: r.filename, data: r.blob })));
        name = `${activeFile!.name.replace(/\.[^.]+$/, '')}_pages.zip`;
        saved = await downloadBlob(zip, name);
      }
      if (saved) announceSaved(applyBtn, name, saved.where);
      else announceCancelled();
    }).catch((err) => showToast(err.message, true));
  });

  applyAllBtn.addEventListener('click', () => {
    const files = fileManager.getFiles();
    if (files.length < 2) return;
    void withProgress('Processing batch…', async (onProgress) => {
      const entries: { filename: string; data: Blob }[] = [];
      for (let i = 0; i < files.length; i++) {
        const results = await applyToFile(files[i]);
        for (const r of results) entries.push({ filename: r.filename, data: r.blob });
        onProgress(i + 1, files.length);
      }
      const zip = await buildZip(entries);
      const saved = await downloadBlob(zip, 'myphototools-dpi.zip');
      if (saved) announceSaved(applyAllBtn, 'myphototools-dpi.zip', saved.where);
      else announceCancelled();
    }).catch((err) => showToast(err.message, true));
  });
}

function buildMarkup(): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'workspace';
  wrapper.innerHTML = `
    <div class="canvas-column">
      <div class="canvas-toolbar" role="toolbar" aria-label="Preview zoom">
        <div class="zoom-controls">
          <button id="zoom-out-dpi" title="Zoom out">−</button>
          <output id="zoom-level-dpi" class="zoom-level">100%</output>
          <button id="zoom-in-dpi" title="Zoom in">+</button>
          <button id="zoom-reset-dpi" title="Fit the photo to the box again (resets zoom and position)">Reset</button>
        </div>
      </div>
      <div class="canvas-viewport canvas-area" aria-label="Loaded image or PDF page preview: scroll wheel or pinch to zoom, drag to pan"></div>
    </div>
    <aside class="side-panel">
      <div class="panel-section">
        <h2>Files</h2>
        <div id="dpi-dropzone" class="dropzone">Drop PNG / JPEG / PDF here, or click to choose</div>
        <ul id="dpi-file-list" class="file-list" aria-label="Loaded files"></ul>
        <div id="dpi-current-readout" class="dims-readout"></div>
      </div>

      <div class="panel-section">
        <h3>Mode</h3>
        <div id="dpi-mode" class="segmented" role="group" aria-label="DPI conversion mode">
          <button data-mode="tagOnly" aria-pressed="true">Change tag only</button>
          <button data-mode="resample" aria-pressed="false">Resample pixels</button>
        </div>
      </div>

      <div class="panel-section">
        <h3>Target DPI</h3>
        <div class="field-row">
          <label for="dpi-preset">Preset</label>
          <select id="dpi-preset">
            <option value="72">72</option>
            <option value="96">96</option>
            <option value="150">150</option>
            <option value="300" selected>300</option>
            <option value="600">600</option>
            <option value="custom">Custom…</option>
          </select>
        </div>
        <div class="field-row">
          <label for="dpi-custom">Custom DPI</label>
          <input type="number" id="dpi-custom" min="1" max="20000" value="300" />
        </div>
        <div class="field-row">
          <label id="dpi-lock-label">Lock</label>
          <div id="dpi-lock" class="segmented" role="group" aria-labelledby="dpi-lock-label">
            <button data-lock="printSize" aria-pressed="true">Print size</button>
            <button data-lock="pixelSize" aria-pressed="false">Pixel size</button>
          </div>
        </div>
        <div class="field-row">
          <label id="dpi-filter-label">Filter</label>
          <div id="dpi-filter" class="segmented" role="group" aria-labelledby="dpi-filter-label">
            <button data-filter="lanczos3" aria-pressed="true">Lanczos3</button>
            <button data-filter="bicubic" aria-pressed="false">Bicubic</button>
          </div>
        </div>
        <div id="dpi-projected-readout" class="dims-readout"></div>
      </div>

      <div id="dpi-pdf-section" class="panel-section" hidden>
        <h3>PDF options</h3>
        <div class="field-row">
          <label id="dpi-pdf-mode-label">PDF action</label>
          <div id="dpi-pdf-mode" class="segmented" role="group" aria-labelledby="dpi-pdf-mode-label">
            <button data-pdfmode="rasterize" aria-pressed="true">Rasterize pages</button>
            <button data-pdfmode="downsample" aria-pressed="false">Downsample images</button>
          </div>
        </div>
        <div class="field-row">
          <label for="dpi-page-range">Page range</label>
          <input type="text" id="dpi-page-range" placeholder="all pages" />
        </div>
        <div class="field-row">
          <label for="dpi-pdf-output-format">Rasterize to</label>
          <select id="dpi-pdf-output-format">
            <option value="png">PNG (one per page)</option>
            <option value="jpeg">JPEG (one per page)</option>
            <option value="pdf">New PDF</option>
          </select>
        </div>
      </div>

      <div class="panel-section">
        <h3>Output</h3>
        <div class="field-row">
          <label for="dpi-out-format">Image format</label>
          <select id="dpi-out-format">
            <option value="png">PNG</option>
            <option value="jpeg">JPEG</option>
          </select>
        </div>
        <div class="field-row" id="dpi-jpeg-quality-row" hidden>
          <label for="dpi-jpeg-quality">JPEG quality</label>
          <input type="range" id="dpi-jpeg-quality" min="0.5" max="1" step="0.01" value="0.92" />
          <output id="dpi-jpeg-quality-out" for="dpi-jpeg-quality">0.92</output>
        </div>
        <div class="field-row">
          <label for="dpi-suffix">Filename suffix</label>
          <input type="text" id="dpi-suffix" placeholder="_300dpi" />
        </div>
        <div class="field-row">
          <label for="dpi-preserve-metadata">Preserve other metadata</label>
          <input type="checkbox" id="dpi-preserve-metadata" checked />
        </div>
        ${saveLocationMarkup('dpi')}
        <div class="btn-row">
          <button id="dpi-apply" class="btn btn-primary">Apply &amp; export</button>
          <button id="dpi-apply-all" class="btn" disabled>Apply to all → ZIP</button>
        </div>
      </div>
    </aside>
  `;
  return wrapper;
}
